import { useEffect, useState } from "react";
import { GraduationCap, Plus, Edit, Trash2, X, Save, Mail, Phone, FileText, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const EMPTY_SCH = {
  title: "", description: "", level: "licence",
  university: "", city: "", country: "Chine", field_of_study: "",
  deadline: "", amount: "", duration: "", image_url: "", apply_url: "",
  is_active: true,
};

const STATUS_LABELS = {
  nouveau: "🆕 Nouveau",
  en_cours: "⏳ En cours",
  accepte: "✅ Accepté",
  refuse: "❌ Refusé",
  inscrit: "🎓 Inscrit",
};

export default function CoifPanel() {
  const [tab, setTab] = useState("applications");
  const [scholarships, setScholarships] = useState([]);
  const [apps, setApps] = useState([]);
  const [form, setForm] = useState(EMPTY_SCH);
  const [editId, setEditId] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/scholarships?active_only=false"),
        api.get("/admin/student-applications"),
      ]);
      setScholarships(s.data || []);
      setApps(a.data || []);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submitSch = async (e) => {
    e.preventDefault(); setErr("");
    try {
      if (editId) await api.put(`/admin/scholarships/${editId}`, form);
      else await api.post("/admin/scholarships", form);
      setForm(EMPTY_SCH); setEditId(null);
      load();
    } catch (er) { setErr(formatApiError(er)); }
  };

  const editSch = (s) => { setForm({ ...EMPTY_SCH, ...s }); setEditId(s.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancelEdit = () => { setForm(EMPTY_SCH); setEditId(null); };

  const deleteSch = async (id) => {
    if (!window.confirm("Supprimer cette bourse ?")) return;
    await api.delete(`/admin/scholarships/${id}`); load();
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, image_url: r.data.url }));
    } catch (er) { setErr(formatApiError(er)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/student-applications/${id}`, { status });
      load();
      if (selectedApp?.id === id) setSelectedApp({ ...selectedApp, status });
    } catch (er) { setErr(formatApiError(er)); }
  };

  const deleteApp = async (id) => {
    if (!window.confirm("Supprimer cette candidature ?")) return;
    await api.delete(`/admin/student-applications/${id}`);
    setSelectedApp(null); load();
  };

  return (
    <div data-testid="coif-panel">
      <div className="flex items-center gap-2 mb-6">
        <GraduationCap className="text-[var(--accent)]" />
        <h2 className="font-display text-2xl font-black">COIF — Étudier en Chine</h2>
      </div>

      <div className="flex gap-2 border-b border-black/10 mb-6">
        <button onClick={() => setTab("applications")} className={`px-4 py-2 text-sm font-bold uppercase tracking-wider ${tab === "applications" ? "text-[var(--accent)] border-b-2 border-[var(--accent)]" : "text-black/50"}`} data-testid="coif-tab-apps">
          Candidatures ({apps.length})
        </button>
        <button onClick={() => setTab("scholarships")} className={`px-4 py-2 text-sm font-bold uppercase tracking-wider ${tab === "scholarships" ? "text-[var(--accent)] border-b-2 border-[var(--accent)]" : "text-black/50"}`} data-testid="coif-tab-sch">
          Bourses & offres ({scholarships.length})
        </button>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm mb-4">{err}</div>}

      {tab === "scholarships" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <form onSubmit={submitSch} className="card-flat p-6 space-y-3 lg:col-span-1" data-testid="scholarship-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold">{editId ? "Modifier la bourse" : "Nouvelle bourse"}</h3>
              {editId && <button type="button" onClick={cancelEdit} className="text-xs text-[var(--ink-soft)] hover:text-black inline-flex items-center gap-1"><X size={14} /> Annuler</button>}
            </div>
            <Input label="Titre" v={form.title} on={(v) => setForm({ ...form, title: v })} required testid="sch-title" />
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)]">Niveau</label>
              <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="input-field mt-1" data-testid="sch-level">
                <option value="langue">Année de langue</option>
                <option value="licence">Licence</option>
                <option value="master">Master</option>
                <option value="doctorat">Doctorat</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)]">Description</label>
              <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-field mt-1" required data-testid="sch-desc" />
            </div>
            <Input label="Université" v={form.university} on={(v) => setForm({ ...form, university: v })} testid="sch-uni" />
            <Input label="Ville" v={form.city} on={(v) => setForm({ ...form, city: v })} testid="sch-city" />
            <Input label="Domaine d'études" v={form.field_of_study} on={(v) => setForm({ ...form, field_of_study: v })} testid="sch-field" />
            <Input label="Montant / bourse" v={form.amount} on={(v) => setForm({ ...form, amount: v })} placeholder="Ex: Bourse totale, 50%" testid="sch-amount" />
            <Input label="Durée" v={form.duration} on={(v) => setForm({ ...form, duration: v })} placeholder="Ex: 4 ans" testid="sch-duration" />
            <Input label="Deadline" type="date" v={form.deadline} on={(v) => setForm({ ...form, deadline: v })} testid="sch-deadline" />
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)]">Image (facultatif)</label>
              <input type="file" accept="image/*" onChange={uploadImage} className="text-xs mt-1" data-testid="sch-image-upload" />
              {uploading && <p className="text-xs text-[var(--ink-soft)] mt-1">Upload…</p>}
              {form.image_url && <img src={form.image_url} alt="preview" className="w-full h-24 object-cover rounded mt-2" />}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="sch-active" />
              Visible publiquement
            </label>
            <button type="submit" className="btn-primary w-full" data-testid="sch-submit">
              <Save size={14} /> {editId ? "Enregistrer" : "Publier"}
            </button>
          </form>

          <div className="lg:col-span-2 space-y-4">
            {scholarships.length === 0 && <p className="text-[var(--ink-soft)] text-sm">Aucune bourse. Publiez la première.</p>}
            {scholarships.map((s) => (
              <div key={s.id} className="card-flat p-5 flex gap-4" data-testid={`sch-item-${s.id}`}>
                {s.image_url && <img src={s.image_url} alt="" className="w-24 h-24 object-cover rounded shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[var(--accent)]/10 text-[var(--accent)]">{s.level}</span>
                    {!s.is_active && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-black/10">Masqué</span>}
                    {s.deadline && <span className="text-[10px] text-[var(--ink-soft)]">📅 {s.deadline}</span>}
                  </div>
                  <p className="font-display font-bold mt-2 truncate">{s.title}</p>
                  <p className="text-xs text-[var(--ink-soft)] mt-1 line-clamp-2">{s.description}</p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => editSch(s)} className="text-xs inline-flex items-center gap-1 hover:text-[var(--accent)]"><Edit size={12} /> Modifier</button>
                    <button onClick={() => deleteSch(s.id)} className="text-xs inline-flex items-center gap-1 hover:text-red-500"><Trash2 size={12} /> Supprimer</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "applications" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-3 max-h-[600px] overflow-y-auto pr-2" data-testid="app-list">
            {apps.length === 0 && <p className="text-[var(--ink-soft)] text-sm">Aucune candidature reçue.</p>}
            {apps.map((a) => (
              <button key={a.id} onClick={() => setSelectedApp(a)} className={`w-full text-left card-flat p-4 hover:border-[var(--accent)] ${selectedApp?.id === a.id ? "border-[var(--accent)]" : ""}`} data-testid={`app-${a.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-bold text-sm">{a.first_name} {a.last_name}</p>
                  <span className="text-[10px]">{STATUS_LABELS[a.status] || a.status}</span>
                </div>
                <p className="text-xs text-[var(--ink-soft)] mt-1">{a.target_level} · {a.field_of_study}</p>
                <p className="text-[10px] text-[var(--ink-soft)] mt-1">{a.reference} · {a.country}</p>
              </button>
            ))}
          </div>

          <div className="lg:col-span-2">
            {!selectedApp ? (
              <div className="card-flat p-10 text-center text-[var(--ink-soft)]">Sélectionnez une candidature à gauche.</div>
            ) : (
              <div className="card-flat p-6 space-y-4" data-testid="app-detail">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-[var(--ink-soft)]">{selectedApp.reference}</p>
                    <h3 className="font-display text-2xl font-black mt-1">{selectedApp.first_name} {selectedApp.last_name}</h3>
                    <p className="text-sm text-[var(--ink-soft)] mt-1">{selectedApp.country} · Reçue le {new Date(selectedApp.created_at).toLocaleDateString()}</p>
                  </div>
                  <button onClick={() => deleteApp(selectedApp.id)} className="text-red-500 hover:text-red-700" data-testid="app-delete"><Trash2 size={16} /></button>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <a href={`mailto:${selectedApp.email}`} className="flex items-center gap-2 hover:text-[var(--accent)]"><Mail size={14} /> {selectedApp.email}</a>
                  <a href={`https://wa.me/${selectedApp.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[var(--accent)]" data-testid="app-whatsapp"><Phone size={14} /> {selectedApp.phone} · WhatsApp</a>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm bg-[var(--bg-soft)] p-4 rounded">
                  <div><strong>Niveau visé :</strong> {selectedApp.target_level}</div>
                  <div><strong>Domaine :</strong> {selectedApp.field_of_study}</div>
                  {selectedApp.current_level && <div><strong>Niveau actuel :</strong> {selectedApp.current_level}</div>}
                  {selectedApp.preferred_city && <div><strong>Ville souhaitée :</strong> {selectedApp.preferred_city}</div>}
                  {selectedApp.academic_year && <div><strong>Année visée :</strong> {selectedApp.academic_year}</div>}
                  {selectedApp.budget && <div><strong>Budget :</strong> {selectedApp.budget}</div>}
                  {selectedApp.date_of_birth && <div><strong>Naissance :</strong> {selectedApp.date_of_birth}</div>}
                </div>

                {selectedApp.message && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)] mb-2">Message</p>
                    <p className="text-sm bg-[var(--bg-soft)] p-4 rounded whitespace-pre-wrap">{selectedApp.message}</p>
                  </div>
                )}

                {selectedApp.documents?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)] mb-2">Documents</p>
                    <ul className="space-y-2" data-testid="app-docs">
                      {selectedApp.documents.map((d, i) => (
                        <li key={i}>
                          <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-sm inline-flex items-center gap-2 hover:text-[var(--accent)]">
                            <FileText size={14} /> {d.name} <ExternalLink size={12} />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)] mb-2">Statut</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(STATUS_LABELS).map(([k, l]) => (
                      <button key={k} onClick={() => setStatus(selectedApp.id, k)} className={`px-3 py-1.5 text-xs font-bold rounded border ${selectedApp.status === k ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "border-black/10 hover:border-[var(--accent)]"}`} data-testid={`app-status-${k}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, v, on, type = "text", required, testid, placeholder }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)]">{label}</label>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} required={required} placeholder={placeholder}
        className="input-field mt-1" data-testid={testid} />
    </div>
  );
}
